import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { ChevronDown, ChevronRight, Eye, Layers, Loader2, Plus, Trash2 } from "lucide-react";
import { api, ApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const ENVS = ["production", "staging", "preview", "development"] as const;
type EnvName = typeof ENVS[number];

interface GroupVar {
  id: string;
  key: string;
  environment: EnvName;
  isSecret: boolean;
  updatedAt: string;
  valueMasked: string;
}

interface EnvGroup {
  id: string;
  name: string;
  createdAt: string;
  vars: GroupVar[];
  linkedProjects: Array<{ projectId: string; slug: string }>;
}

/**
 * 008 (US2) — org-level shared environment variables.
 *
 * Values are write-only over the API: the list returns masked entries and
 * revealing one is a separate authenticated call, matching how project env
 * vars behave.
 */
export function EnvGroups() {
  const { org } = useParams<{ org: string }>();
  const qc = useQueryClient();
  const [openId, setOpenId] = useState<string | null>(null);
  const [newName, setNewName] = useState("");
  const [revealed, setRevealed] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);

  const invalidate = () => qc.invalidateQueries({ queryKey: ["env-groups", org] });
  const onError = (e: unknown) =>
    setError(e instanceof ApiError ? e.message : "something went wrong");

  const groups = useQuery({
    queryKey: ["env-groups", org],
    queryFn: () => api.get<{ groups: EnvGroup[] }>(`/orgs/${org}/env-groups`).then((r) => r.groups),
  });

  const createGroup = useMutation({
    mutationFn: (name: string) => api.post(`/orgs/${org}/env-groups`, { name }),
    onSuccess: () => { setNewName(""); setError(null); invalidate(); },
    onError,
  });
  const deleteGroup = useMutation({
    mutationFn: (id: string) => api.delete(`/orgs/${org}/env-groups/${id}`),
    onSuccess: () => { setError(null); invalidate(); },
    onError,
  });
  const deleteVar = useMutation({
    mutationFn: ({ groupId, varId }: { groupId: string; varId: string }) =>
      api.delete(`/orgs/${org}/env-groups/${groupId}/vars/${varId}`),
    onSuccess: () => { setError(null); invalidate(); },
    onError,
  });
  const reveal = useMutation({
    mutationFn: ({ groupId, varId }: { groupId: string; varId: string }) =>
      api.post<{ key: string; value: string }>(`/orgs/${org}/env-groups/${groupId}/vars/${varId}/reveal`),
    onSuccess: (r, v) => setRevealed((prev) => ({ ...prev, [v.varId]: r.value })),
    onError,
  });

  if (groups.isLoading) {
    return <div className="flex items-center gap-2 p-6 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>;
  }

  return (
    <div className="space-y-6 p-6">
      <div>
        <h2 className="text-lg font-semibold">Environment groups</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Shared variables you can link to any project in this org. A project&rsquo;s own
          variables always override what it inherits.
        </p>
      </div>

      {error && (
        <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {error}
        </div>
      )}

      <form
        className="flex items-end gap-2"
        onSubmit={(e) => { e.preventDefault(); if (newName.trim()) createGroup.mutate(newName.trim()); }}
      >
        <div className="flex-1">
          <Label htmlFor="group-name">New group</Label>
          <Input
            id="group-name"
            value={newName}
            placeholder="Shared Infra"
            onChange={(e) => setNewName(e.target.value)}
          />
        </div>
        <Button type="submit" disabled={!newName.trim() || createGroup.isPending}>
          <Plus className="mr-1 h-4 w-4" /> Create
        </Button>
      </form>

      {groups.data?.length === 0 && (
        <div className="rounded-md border border-dashed px-4 py-8 text-center text-sm text-muted-foreground">
          <Layers className="mx-auto mb-2 h-5 w-5" />
          No environment groups yet.
        </div>
      )}

      <div className="space-y-3">
        {groups.data?.map((g) => (
          <div key={g.id} className="rounded-md border">
            <div className="flex items-center gap-2 px-3 py-2">
              <button
                type="button"
                className="flex flex-1 items-center gap-2 text-left"
                onClick={() => setOpenId(openId === g.id ? null : g.id)}
              >
                {openId === g.id ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                <span className="font-medium">{g.name}</span>
                <span className="text-xs text-muted-foreground">
                  {g.vars.length} var{g.vars.length === 1 ? "" : "s"} ·{" "}
                  {g.linkedProjects.length} project{g.linkedProjects.length === 1 ? "" : "s"}
                </span>
              </button>
              <Button
                variant="ghost"
                size="sm"
                aria-label={`Delete ${g.name}`}
                onClick={() => {
                  if (confirm(`Delete "${g.name}"? It will be unlinked from ${g.linkedProjects.length} project(s).`)) {
                    deleteGroup.mutate(g.id);
                  }
                }}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>

            {openId === g.id && (
              <div className="border-t px-3 py-3">
                {g.linkedProjects.length > 0 && (
                  <p className="mb-3 text-xs text-muted-foreground">
                    Linked to {g.linkedProjects.map((p) => p.slug).join(", ")}
                  </p>
                )}

                <table className="w-full text-sm">
                  <thead className="text-xs uppercase text-muted-foreground">
                    <tr>
                      <th className="py-1 text-left font-medium">Key</th>
                      <th className="py-1 text-left font-medium">Value</th>
                      <th className="py-1 text-left font-medium">Environment</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {g.vars.map((v) => (
                      <tr key={v.id} className="border-t">
                        <td className="py-1.5 font-mono text-xs">{v.key}</td>
                        <td className="py-1.5 font-mono text-xs">
                          {revealed[v.id] ?? v.valueMasked}
                        </td>
                        <td className="py-1.5 text-xs text-muted-foreground">
                          {v.environment}
                        </td>
                        <td className="py-1.5 text-right">
                          {revealed[v.id] === undefined && (
                            <Button
                              variant="ghost"
                              size="sm"
                              aria-label={`Reveal ${v.key}`}
                              onClick={() => reveal.mutate({ groupId: g.id, varId: v.id })}
                            >
                              <Eye className="h-3.5 w-3.5" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            aria-label={`Delete ${v.key}`}
                            onClick={() => deleteVar.mutate({ groupId: g.id, varId: v.id })}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <AddVarForm orgSlug={org!} groupId={g.id} onDone={invalidate} onError={onError} />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function AddVarForm({
  orgSlug,
  groupId,
  onDone,
  onError,
}: {
  orgSlug: string;
  groupId: string;
  onDone: () => void;
  onError: (e: unknown) => void;
}) {
  const [key, setKey] = useState("");
  const [value, setValue] = useState("");
  const [envs, setEnvs] = useState<EnvName[]>([...ENVS]);

  const save = useMutation({
    mutationFn: () =>
      api.put(`/orgs/${orgSlug}/env-groups/${groupId}/vars`, {
        key,
        value,
        environments: envs,
        isSecret: true,
      }),
    onSuccess: () => { setKey(""); setValue(""); onDone(); },
    onError,
  });

  return (
    <form
      className="mt-3 flex flex-wrap items-end gap-2 border-t pt-3"
      onSubmit={(e) => { e.preventDefault(); if (key && envs.length) save.mutate(); }}
    >
      <div className="w-44">
        <Label htmlFor={`k-${groupId}`}>Key</Label>
        <Input id={`k-${groupId}`} value={key} placeholder="DATABASE_URL" onChange={(e) => setKey(e.target.value)} />
      </div>
      <div className="w-64 flex-1">
        <Label htmlFor={`v-${groupId}`}>Value</Label>
        <Input id={`v-${groupId}`} type="password" value={value} onChange={(e) => setValue(e.target.value)} />
      </div>
      <div className="flex items-center gap-3 pb-2">
        {ENVS.map((e) => (
          <label key={e} className="flex items-center gap-1 text-xs">
            <input
              type="checkbox"
              checked={envs.includes(e)}
              onChange={(ev) =>
                setEnvs((prev) => (ev.target.checked ? [...prev, e] : prev.filter((x) => x !== e)))
              }
            />
            {e}
          </label>
        ))}
      </div>
      <Button type="submit" size="sm" disabled={!key || envs.length === 0 || save.isPending}>
        Save
      </Button>
    </form>
  );
}
